import qs from 'qs';

// A stored relay marker is only honored for this long. It bounds the window in
// which an abandoned MCP login (marker persisted, then flow dropped) could have
// a later same-tab login's JWT forwarded to the loopback listener. Keep this
// <= the MCP listener timeout (ssologin defaultTimeout) so the browser never
// forwards to a port the MCP has already released. Both are aligned at 4m, held
// under the MCP client's hard 300s call ceiling.
export const MCP_RELAY_TTL_MS = 4 * 60 * 1000;

// Relay helpers for the local admin-mcp browser-login flow (PAB-8053). The MCP
// opens this SSO page with ?mcp=1&port=<port>&nonce=<nonce>, having started a
// loopback listener on <port>; after a normal Employee Login the page forwards
// the freshly minted app JWT back to that listener.

// getMcpRelayFromQuery returns { port, nonce } when the page was opened by the
// MCP flow, else null. `search` defaults to the current location query string
// (injectable for tests).
export function getMcpRelayFromQuery(search) {
  const raw =
    typeof search === 'string'
      ? search
      : typeof window !== 'undefined'
        ? window.location.search
        : '';
  const q = qs.parse(raw, { ignoreQueryPrefix: true });
  if (q.mcp !== '1') return null;
  return { port: q.port, nonce: q.nonce };
}

// isRelayFresh reports whether a stored marker (with a `ts` set when it was
// created) is within the TTL, so a stale marker from an abandoned flow is not
// consumed by an unrelated later login.
export function isRelayFresh(relay, nowMs) {
  const ts = Number(relay && relay.ts);
  if (!Number.isFinite(ts) || ts <= 0) return false;
  const age = nowMs - ts;
  return age >= 0 && age <= MCP_RELAY_TTL_MS;
}

// buildMcpCallbackURL returns the loopback callback URL that hands the app JWT
// to the local admin-mcp listener, or null if the marker/token is invalid.
//
// SECURITY: the scheme, host, and path are hard-coded to http://127.0.0.1/cb
// and only a validated numeric port is taken from the marker — never a host or
// URL from any parameter — so this can never be turned into an open redirect
// that exfiltrates the app JWT. The nonce ties the callback to the MCP instance
// that initiated the login.
export function buildMcpCallbackURL(relay, token) {
  if (!relay || !relay.nonce || !token) return null;
  const port = Number(relay.port);
  if (!Number.isInteger(port) || port < 1024 || port > 65535) return null;
  return (
    'http://127.0.0.1:' +
    port +
    '/cb?token=' +
    encodeURIComponent(token) +
    '&nonce=' +
    encodeURIComponent(relay.nonce)
  );
}
